// Inbound Shipment (進貨單) Types

// IQC Status for quality inspection
export type IQCStatus = 'pending' | 'passed' | 'failed' | 'waived';

// Inbound Shipment Status
export type InboundStatus = 'draft' | 'pending_iqc' | 'iqc_passed' | 'iqc_failed' | 'completed' | 'cancelled';

// Received item within an inbound shipment
export interface InboundItem {
  line_no: number;              // Line number from PO
  product_name: string;         // 品名
  specification: string;        // 規格
  po_quantity: number;          // 訂購數量 (from PO)
  received_quantity: number;    // 實收數量
  unit_price: number;           // 單價
  amount: number;               // 金額
  currency: string;             // 幣別
  iqc_status: IQCStatus;        // IQC 狀態
  iqc_notes?: string;           // IQC 備註
  remarks?: string;             // 備註
}

// Inbound Shipment document
export interface InboundShipment {
  inbound_id: string;
  inbound_number: string;       // 進貨單號 e.g., "GR-202601-0001"
  po_id: string;                // Reference to PO
  po_number: string;            // PO number for display
  vendor_code: string;          // 廠商代號
  vendor_name?: string;         // 廠商名稱 (enriched)
  received_date: string;        // 進貨日期
  items: InboundItem[];         // 進貨項目
  total_amount: number;         // 總金額
  currency: string;             // 幣別
  status: InboundStatus;        // 進貨單狀態
  flow_id?: string;
  iqc_completed_at?: string;    // IQC 完成時間
  inventory_updated: boolean;   // 是否已更新庫存
  created_by: string;
  created_at: string;
  updated_at: string;
  notes?: string;
}

// Create Inbound Request
export interface CreateInboundRequest {
  po_id: string;
  received_date: string;
  items: Array<{
    line_no: number;
    received_quantity: number;
    remarks?: string;
  }>;
  notes?: string;
}

// Update IQC Status Request
export interface UpdateIQCRequest {
  line_no: number;
  iqc_status: IQCStatus;
  iqc_notes?: string;
}

// Complete IQC Request (batch update all items)
export interface CompleteIQCRequest {
  items: Array<{
    line_no: number;
    iqc_status: IQCStatus;
    iqc_notes?: string;
  }>;
}

// Inbound List Response
export interface InboundListResponse {
  inbound_shipments: InboundShipment[];
  total: number;
  limit: number;
  offset: number;
}

// Inbound Filter
export interface InboundFilter {
  po_id?: string;
  vendor_code?: string;
  status?: InboundStatus;
  start_date?: string;
  end_date?: string;
  limit?: number;
  offset?: number;
}

// Status display labels (Chinese)
export const INBOUND_STATUS_LABELS: Record<InboundStatus, string> = {
  draft: '草稿',
  pending_iqc: '待檢驗',
  iqc_passed: '檢驗通過',
  iqc_failed: '檢驗不合格',
  completed: '已入庫',
  cancelled: '已取消',
};

export const IQC_STATUS_LABELS: Record<IQCStatus, string> = {
  pending: '待檢驗',
  passed: '合格',
  failed: '不合格',
  waived: '免檢',
};

// Status badge colors
export const INBOUND_STATUS_COLORS: Record<InboundStatus, string> = {
  draft: 'bg-gray-100 text-gray-800',
  pending_iqc: 'bg-yellow-100 text-yellow-800',
  iqc_passed: 'bg-green-100 text-green-800',
  iqc_failed: 'bg-red-100 text-red-800',
  completed: 'bg-blue-100 text-blue-800',
  cancelled: 'bg-gray-100 text-gray-500',
};

export const IQC_STATUS_COLORS: Record<IQCStatus, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  passed: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  waived: 'bg-gray-100 text-gray-600',
};
